import { ModuleColumnDefinition } from '../module';
import { FieldTypeEnum } from '../custom-field.types';
import {
  ContractRenewalStatusEnum,
  ContractSowStatusEnum,
  DEFAULT_CONTRACT_LEGAL_STATUSES,
  CONTRACT_RENEWAL_STATUS_COLORS,
  CONTRACT_RENEWAL_STATUS_DISPLAY_NAMES,
  CONTRACT_SOW_STATUS_COLORS,
  CONTRACT_SOW_STATUS_DISPLAY_NAMES,
} from '../contract';

export const CONTRACT_STANDARD_COLUMNS: ModuleColumnDefinition[] = [
  // System columns (always visible)
  {
    id: 'title',
    name: 'title',
    label: 'Contract Title',
    fieldType: FieldTypeEnum.enum.STRING,
    isSystem: true,
    isFixed: true,
    isVisible: true,
    isRequired: true,
    order: 1,
    width: '300px',
    minWidth: '200px'
  },
  
  // Standard columns
  {
    id: 'entityId',
    name: 'entityId',
    label: 'Client',
    description: 'Client associated with this contract',
    fieldType: FieldTypeEnum.enum.DROPDOWN,
    isSystem: false,
    isFixed: false,
    isVisible: true,
    isRequired: false,
    order: 2,
    width: '220px',
    minWidth: '160px'
  },
  {
    id: 'projectId',
    name: 'projectId',
    label: 'Project',
    description: 'Project associated with this contract',
    fieldType: FieldTypeEnum.enum.DROPDOWN,
    isSystem: false,
    isFixed: false,
    isVisible: false,
    isRequired: false,
    order: 3,
    width: '220px',
    minWidth: '160px'
  },
  {
    id: 'contractNumber',
    name: 'contractNumber',
    label: 'Contract Number',
    fieldType: FieldTypeEnum.enum.STRING,
    isSystem: false,
    isFixed: false,
    isVisible: true,
    isRequired: false,
    order: 4,
    width: '150px',
    minWidth: '100px'
  },
  {
    id: 'description',
    name: 'description',
    label: 'Description',
    fieldType: FieldTypeEnum.enum.TEXT,
    isSystem: false,
    isFixed: false,
    isVisible: true,
    isRequired: false,
    order: 5,
    width: '350px',
    minWidth: '200px'
  },
  {
    id: 'status',
    name: 'status',
    label: 'Legal Status',
    fieldType: FieldTypeEnum.enum.DROPDOWN,
    isSystem: false,
    isFixed: false,
    isVisible: true,
    isRequired: false,
    order: 6,
    width: '130px',
    minWidth: '100px',
    options: DEFAULT_CONTRACT_LEGAL_STATUSES.map(status => ({
      value: status.id,
      label: status.name,
      color: status.color,
    })),
  },
  {
    id: 'tags',
    name: 'tags',
    label: 'Tags',
    description: 'Tags for organizing and categorizing the contract',
    fieldType: FieldTypeEnum.enum.STRING,
    isSystem: true,
    isFixed: false,
    isVisible: false,
    isRequired: false,
    order: 90, // Positioned near end of standard columns, custom fields come after at 91+
    width: '200px',
    minWidth: '150px'
  },
  {
    id: 'sowStatus',
    name: 'sowStatus',
    label: 'SOW Status',
    description: 'Operational statement of work workflow status',
    fieldType: FieldTypeEnum.enum.DROPDOWN,
    isSystem: false,
    isFixed: false,
    isVisible: true,
    isRequired: false,
    order: 7,
    width: '150px',
    minWidth: '120px',
    options: Object.values(ContractSowStatusEnum).map(status => ({
      value: status,
      label: CONTRACT_SOW_STATUS_DISPLAY_NAMES[status],
      color: CONTRACT_SOW_STATUS_COLORS[status],
    })),
  },
  {
    id: 'renewalStatus',
    name: 'renewalStatus',
    label: 'Renewal Status',
    description: 'Operational renewal workflow status',
    fieldType: FieldTypeEnum.enum.DROPDOWN,
    isSystem: false,
    isFixed: false,
    isVisible: true,
    isRequired: false,
    order: 8,
    width: '150px',
    minWidth: '120px',
    options: Object.values(ContractRenewalStatusEnum).map(status => ({
      value: status,
      label: CONTRACT_RENEWAL_STATUS_DISPLAY_NAMES[status],
      color: CONTRACT_RENEWAL_STATUS_COLORS[status],
    })),
  },
  {
    id: 'renewalOwnerId',
    name: 'renewalOwnerId',
    label: 'Renewal Owner',
    description: 'Internal owner accountable for renewal follow-up',
    fieldType: FieldTypeEnum.enum.USER,
    isSystem: false,
    isFixed: false,
    isVisible: false,
    isRequired: false,
    order: 9,
    width: '180px',
    minWidth: '140px',
  },
  {
    id: 'renewalDueDate',
    name: 'renewalDueDate',
    label: 'Renewal Due',
    description: 'Target date for renewal action',
    fieldType: FieldTypeEnum.enum.DATE,
    isSystem: false,
    isFixed: false,
    isVisible: false,
    isRequired: false,
    order: 10,
    width: '150px',
    minWidth: '120px',
  },
  {
    id: 'totalValue',
    name: 'totalValue',
    label: 'Total Value',
    fieldType: FieldTypeEnum.enum.NUMBER,
    isSystem: false,
    isFixed: false,
    isVisible: true,
    isRequired: false,
    order: 11,
    width: '150px',
    minWidth: '100px'
  },
  {
    id: 'startDate',
    name: 'startDate',
    label: 'Start Date',
    fieldType: FieldTypeEnum.enum.DATE,
    isSystem: false,
    isFixed: false,
    isVisible: true,
    isRequired: false,
    order: 12,
    width: '150px',
    minWidth: '120px'
  },
  {
    id: 'endDate',
    name: 'endDate',
    label: 'End Date',
    fieldType: FieldTypeEnum.enum.DATE,
    isSystem: false,
    isFixed: false,
    isVisible: true,
    isRequired: false,
    order: 13,
    width: '150px',
    minWidth: '120px'
  },
  {
    id: 'contractType',
    name: 'contractType',
    label: 'Contract Type',
    fieldType: FieldTypeEnum.enum.DROPDOWN,
    isSystem: false,
    isFixed: false,
    isVisible: true,
    isRequired: false,
    order: 14,
    width: '150px',
    minWidth: '100px'
  },
  {
    id: 'paymentTerms',
    name: 'paymentTerms',
    label: 'Payment Terms',
    fieldType: FieldTypeEnum.enum.TEXT,
    isSystem: false,
    isFixed: false,
    isVisible: false,
    isRequired: false,
    order: 15,
    width: '200px',
    minWidth: '150px'
  },
  {
    id: 'isRenewable',
    name: 'isRenewable',
    label: 'Renewable',
    fieldType: FieldTypeEnum.enum.BOOLEAN,
    isSystem: false,
    isFixed: false,
    isVisible: false,
    isRequired: false,
    order: 16,
    width: '100px',
    minWidth: '80px'
  },
  
  // Metadata columns
  {
    id: 'createdAt',
    name: 'createdAt',
    label: 'Created',
    fieldType: FieldTypeEnum.enum.DATE,
    isSystem: false,
    isFixed: false,
    isVisible: false,
    isRequired: false,
    order: 17,
    width: '150px',
    minWidth: '120px'
  },
  {
    id: 'updatedAt',
    name: 'updatedAt',
    label: 'Updated',
    fieldType: FieldTypeEnum.enum.DATE,
    isSystem: false,
    isFixed: false,
    isVisible: false,
    isRequired: false,
    order: 18,
    width: '150px',
    minWidth: '120px'
  }
];
